import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { ChevronDown, Settings, LogOut, Moon, Sun, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useDarkMode } from '../context/DarkModeContext';

export function UserMenu() {
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  const handleLogout = () => {
    setOpen(false);
    logout();
    navigate('/login');
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        aria-label="Konto-Menü"
        title={user.contactPerson}
        className={`flex items-center gap-1 px-2 py-2 rounded-lg transition-colors cursor-pointer ${
          darkMode
            ? 'hover:bg-slate-700 text-slate-200'
            : 'hover:bg-slate-100 text-slate-700'
        }`}
      >
        <User size={22} />
        <ChevronDown
          size={16}
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          className={`absolute right-0 mt-1 w-56 rounded-lg border shadow-lg z-50 py-1 ${
            darkMode
              ? 'bg-slate-800 border-slate-700'
              : 'bg-white border-slate-200'
          }`}
        >
          <div className="px-3 py-2">
            <div className={`text-sm font-medium truncate ${
              darkMode ? 'text-slate-200' : 'text-slate-800'
            }`}>
              {user.contactPerson}
            </div>
            <div className={`text-xs ${
              darkMode ? 'text-slate-400' : 'text-slate-500'
            }`}>
              Konto verwalten
            </div>
          </div>

          <button
            onClick={() => {
              setOpen(false);
              navigate('/settings');
            }}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors cursor-pointer ${
              darkMode
                ? 'text-slate-200 hover:bg-slate-700'
                : 'text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Settings size={16} />
            Einstellungen
          </button>

          <button
            onClick={() => {
              toggleDarkMode();
              setOpen(false);
            }}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
              darkMode
                ? 'text-slate-200 hover:bg-slate-700'
                : 'text-slate-700 hover:bg-slate-50'
            }`}
          >
            <span className="inline-flex">
              {darkMode ? <Sun size={16} /> : <Moon size={16} />}
            </span>
            <span>{darkMode ? 'Tagesansicht' : 'Nachtansicht'}</span>
          </button>

          <div
            className={`my-1 border-t ${
              darkMode ? 'border-slate-700' : 'border-slate-200'
            }`}
          />

          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
              darkMode
                ? 'text-red-400 hover:bg-slate-700'
                : 'text-red-600 hover:bg-red-50'
            }`}
          >
            <LogOut size={16} />
            Abmelden
          </button>
        </div>
      )}
    </div>
  );
}
